import React, { useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  IconButton,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalculationTypeDetailsDrawer from "./CalculationTypeDetails/CalculationTypeDetailsDrawer";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setRowsPerPage,
  setPage,
  setSort,
  setSelectedRow,
  setOpenModalType,
} from "../../../../features/unifierCalculationTypesSlice";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";

// headers for the table columns with sortable option for each column
const tableheaders = [
  { id: 1, KEY: "id", label: "ID", sortable: true, align: "left" as const },
  {
    id: 2,
    KEY: "name", // Use the nested key
    label: "Name",
    sortable: true,
    align: "left" as const,
  },
  {
    id: 3,
    KEY: "description",
    label: "Description",
    sortable: true,
    align: "left" as const,
  },
  {
    id: 4,
    KEY: "has_missed_run_warning",
    label: "Missed Run",
    sortable: false,
    align: "center" as const,
  },
  {
    id: 5,
    KEY: "deep_view_is_documented",
    label: "Documented",
    sortable: false,
    align: "center" as const,
  },
  {
    id: 6,
    KEY: "is_active",
    label: "Active",
    sortable: false,
    align: "center" as const,
  },
  {
    id: 7,
    KEY: "edit",
    label: "Edit",
    sortable: false,
    align: "center" as const,
  },
];

// the flags arrive as 1/0 from the API
const BooleanFlagCell = ({ value }: { value: unknown }) =>
  value ? (
    <CheckIcon fontSize="small" sx={{ color: "success.main" }} />
  ) : (
    <CloseIcon fontSize="small" sx={{ color: "error.main" }} />
  );

// a type without a missed-run warning shows nothing rather than an "Off" state
const MissedRunCell = ({ value }: { value: unknown }) =>
  value ? (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
    >
      <WarningAmberIcon fontSize="small" sx={{ color: "warning.main" }} />
      <Typography variant="body2" sx={{ color: "warning.main" }}>
        On
      </Typography>
    </Stack>
  ) : null;

const CalculationTypesTable = () => {
  const {
    unifierCalculationTypesList,
    sort,
    page,
    rowsPerPage,
    selectedCalculationName,
    selectedMissedRun,
    selectedDocumented,
    selectedActive,
  } = useSelector((state: any) => state.unifierCalculationTypes);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  // the row whose read-only details drawer is open
  const [detailsRow, setDetailsRow] = React.useState<any>(null);

  const response = useSelector(selectUserData);
  const hasUnifierCreateEditCalculationTypesPermission = CheckPermissions(
    response?.data?.products?.data_collection,
    "unifier",
    "create/edit_calculation_types",
  );

  //-- Sorting Data
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        // Default sorting by start_time (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue, bValue;

        aValue = a[keytoSort];
        bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    // eslint-disable-next-line
    [sort],
  );

  const filteredData = React.useMemo(() => {
    const filtered = (unifierCalculationTypesList ?? []).filter((row: any) => {
      const calculationName = selectedCalculationName
        ? row?.name === selectedCalculationName
        : true;
      const missedRun =
        selectedMissedRun && selectedMissedRun.length !== 0
          ? selectedMissedRun.includes(
              row?.has_missed_run_warning ? "On" : "Off",
            )
          : true;
      const documented =
        selectedDocumented && selectedDocumented.length !== 0
          ? selectedDocumented.includes(
              row?.deep_view_is_documented ? "Yes" : "No",
            )
          : true;
      const active =
        selectedActive && selectedActive.length !== 0
          ? selectedActive.includes(row?.is_active ? "Active" : "Inactive")
          : true;

      return calculationName && missedRun && documented && active;
    });

    return getSortedData(filtered);
  }, [
    unifierCalculationTypesList,
    selectedCalculationName,
    selectedMissedRun,
    selectedDocumented,
    selectedActive,
    getSortedData,
  ]);

  const handleOpenModal = (row: any) => {
    dispatch(setOpenModalType("edit"));
    dispatch(setSelectedRow(row)); // Set the selected row
  };

  // -- Function to handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", newPage.toString());
    setSearchParams(updatedParams);
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };
  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      }),
    );
  };

  useEffect(() => {
    dispatch(setPage(Number(searchParams.get("page")) || 0));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <>
      <Paper sx={{ mt: 2, boxShadow: "none" }}>
        <TableContainer className="" component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableheaders.map((header: any) => (
                  <TableCell
                    key={header.id}
                    align={header.align}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={tableheaders.length}
                    align="center"
                    sx={{ padding: "20px", color: "#888", fontSize: "18px" }}
                  >
                    No Data To Display
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {filteredData.length > 0 &&
                    filteredData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                          <TableRow
                            sx={{
                              "& > *": {
                                borderBottom:
                                  "1px solid rgba(224, 224, 224, 1) !important",
                              },
                            }}
                          >
                            <TableCell align="left">{row.id ?? ""}</TableCell>
                            <TableCell align="left">
                              <Typography
                                variant="body2"
                                component="span"
                                role="button"
                                tabIndex={0}
                                onClick={() => setDetailsRow(row)}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                  ) {
                                    event.preventDefault();
                                    setDetailsRow(row);
                                  }
                                }}
                                sx={{
                                  color: "secondary.main",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                {row.name ?? ""}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              {row.description ?? ""}
                            </TableCell>
                            <TableCell align="center">
                              <MissedRunCell
                                value={row.has_missed_run_warning}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <BooleanFlagCell
                                value={row.deep_view_is_documented}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <BooleanFlagCell value={row.is_active} />
                            </TableCell>

                            <TableCell align="center">
                              {hasUnifierCreateEditCalculationTypesPermission ? (
                                <Tooltip title="Edit" placement="top">
                                  <IconButton
                                    data-tracker="378"
                                    sx={{ width: "40px", height: "40px" }}
                                    onClick={() => handleOpenModal(row)}
                                  >
                                    <EditIcon
                                      color="secondary"
                                      fontSize="small"
                                    />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Edit" placement="top">
                                  <IconButton
                                    sx={{ width: "40px", height: "40px" }}
                                    disabled
                                  >
                                    <EditIcon
                                      color="disabled"
                                      fontSize="small"
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                </>
              )}
            </TableBody>
          </Table>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={filteredData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                  {
                    display: "none",
                  },
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                },
              }}
            />
            <Pagination
              count={Math.ceil(filteredData?.length / rowsPerPage)}
              onChange={(_, value) => handleChangePage(null, value - 1)}
              page={page + 1}
              showFirstButton
              showLastButton
              className="pagination-button"
              color="secondary"
              size="small"
              sx={{
                ".css-wjh20t-MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={filteredData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              classes={{
                displayedRows: "hidden-displayed-rows", // Apply the custom class
              }}
              sx={{
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-input": {
                  marginRight: "15px",
                },
              }}
            />
          </Stack>
        </TableContainer>
      </Paper>
      <CalculationTypeDetailsDrawer
        open={Boolean(detailsRow)}
        calculationType={detailsRow}
        onClose={() => setDetailsRow(null)}
      />
    </>
  );
};

export default CalculationTypesTable;
